import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

export const NotificationsMenu = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    load();
    navigate(n.link);
  };
  const readAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell-btn"
          aria-label="Notifications"
          className="relative grid h-10 w-10 place-items-center rounded-full border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
        >
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notifications-unread-badge" className="absolute -top-1 -right-1 grid h-5 min-w-5 place-items-center rounded-full bg-[#E50914] px-1 text-[0.68rem] font-bold text-white shadow-[0_0_12px_rgba(229,9,20,0.7)]">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={12} className="w-[340px] sm:w-[380px] p-0 glass bg-[#0B0B0D]/95 border-[#D4AF37]/30 text-white" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#D4AF37]/15">
          <span className="eyebrow">Notifications</span>
          <button data-testid="notifications-mark-all-btn" onClick={readAll} className="flex items-center gap-1 text-xs text-[#8B8B8B] hover:text-[#FFD86A] transition-colors">
            <CheckCheck className="h-4 w-4" /> Mark all read
          </button>
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {data.items.map((n) => (
            <button
              key={n.id}
              data-testid={`notification-item-${n.id}`}
              onClick={() => openItem(n)}
              className="flex w-full gap-3 px-4 py-3 text-left hover:bg-[#D4AF37]/[0.07] transition-colors"
            >
              <img src={n.image} alt="" className="h-14 w-10 flex-none rounded object-cover border border-[#D4AF37]/25" />
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2">
                  {!n.read && <span className="h-2 w-2 flex-none rounded-full bg-[#E50914]" data-testid="notification-unread-dot" />}
                  <span className={`text-sm font-semibold truncate ${n.read ? "text-white/70" : "text-white"}`}>{n.title}</span>
                </span>
                <span className="mt-0.5 block text-xs text-[#8B8B8B] line-clamp-2">{n.body}</span>
                <span className="mt-1 block text-[0.7rem] text-[#D4AF37]/70">{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</span>
              </span>
            </button>
          ))}
        </div>
        <Link to="/notifications" onClick={() => setOpen(false)} data-testid="notifications-view-all" className="block border-t border-[#D4AF37]/15 px-4 py-3 text-center text-xs font-bold tracking-[0.2em] text-[#D4AF37] hover:text-[#FFD86A]">VIEW ALL</Link>
      </PopoverContent>
    </Popover>
  );
};
